import type { CSSProperties } from "react";
import {
  IconBell,
  IconBookmark,
  IconCompass,
  IconFlame,
  IconHome,
  IconLibrary,
} from "../common/Icons";
import { PINK, VARIANT, type VariantId } from "../../lib/tokens";
import { LEGAL_URLS, PLATFORM_BASE_URL } from "../../lib/platform-urls";
import { ScrollyLogo } from "./ScrollyLogo";

// Session A: AGB + Datenschutz verlinken auf die kanonischen Plattform-
// Routen. Über uns/Presse/Creator bleiben bewusst ohne Ziel (separates Thema).
const SIDEBAR_FOOTER_LINKS: ReadonlyArray<{ label: string; href?: string }> = [
  { label: "Über uns" },
  { label: "Presse" },
  { label: "Creator" },
  { label: "AGB", href: LEGAL_URLS.terms },
  { label: "Datenschutz", href: LEGAL_URLS.privacy },
];

type NavItem = {
  id: string;
  label: string;
  icon: React.ReactNode;
  requiresAuth: boolean;
  /** Target-URL oder `null` für noch nicht geroutete Items (bleiben Buttons). */
  href: string | null;
};

// Phase 11 Step 4 Block 2 — nur "Startseite" ist real geroutet;
// die anderen Items sind Platzhalter für spätere Feature-Routen (Entdecken,
// Im Trend etc. liegen in /category/* oder folgen als eigene Pages).
const NAV: NavItem[] = [
  { id: "home", label: "Startseite", icon: <IconHome size={20} />, requiresAuth: false, href: "/" },
  { id: "explore", label: "Entdecken", icon: <IconCompass size={20} />, requiresAuth: false, href: null },
  { id: "trending", label: "Im Trend", icon: <IconFlame size={20} />, requiresAuth: false, href: null },
  { id: "subs", label: "Abonniert", icon: <IconBell size={20} />, requiresAuth: true, href: null },
  { id: "library", label: "Bibliothek", icon: <IconLibrary size={20} />, requiresAuth: true, href: null },
  // "Gespeichert" (2026-09-17): die Sammelstelle lebt im Portal (/saved), nicht im Viewer.
  { id: "saved", label: "Gespeichert", icon: <IconBookmark size={20} />, requiresAuth: true, href: `${PLATFORM_BASE_URL}/saved` },
];

export function Sidebar({
  variant,
  loggedIn,
  currentId = "home",
}: {
  variant: VariantId;
  loggedIn: boolean;
  currentId?: string;
}) {
  const v = VARIANT[variant];
  const isDark = variant === "immersive";

  const items = NAV.filter((n) => !n.requiresAuth || loggedIn);

  return (
    <aside
      style={{
        width: 232,
        flexShrink: 0,
        background: v.sidebarBg,
        borderRight: `1px solid ${v.border}`,
        padding: "24px 12px 24px",
        display: "flex",
        flexDirection: "column",
        position: "sticky",
        top: 0,
        height: "100vh",
        overflow: "auto",
      }}
    >
      <div style={{ padding: "0 8px 24px", display: "flex", alignItems: "center", gap: 8 }}>
        <ScrollyLogo isDark={isDark} />
      </div>

      <nav style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {items.map((n) => {
          const active = currentId === n.id;
          const navStyle: CSSProperties = {
            display: "flex",
            alignItems: "center",
            gap: 14,
            padding: "10px 12px",
            borderRadius: 10,
            border: "none",
            background: active ? v.sidebarActive : "transparent",
            color: active ? PINK : v.text,
            fontSize: 14,
            fontWeight: active ? 600 : 500,
            cursor: "pointer",
            textAlign: "left",
            fontFamily: "inherit",
            transition: "background 120ms",
            textDecoration: "none",
          };
          if (n.href) {
            return (
              <a key={n.id} href={n.href} style={navStyle}>
                {n.icon}
                <span>{n.label}</span>
              </a>
            );
          }
          return (
            <button key={n.id} style={navStyle}>
              {n.icon}
              <span>{n.label}</span>
            </button>
          );
        })}
      </nav>

      <div style={{ flex: 1 }} />

      <div
        style={{
          padding: 12,
          fontSize: 11,
          color: v.muted,
          lineHeight: 1.6,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            marginBottom: 10,
          }}
        >
          {SIDEBAR_FOOTER_LINKS.map((l) =>
            l.href ? (
              <a
                key={l.label}
                href={l.href}
                style={{ color: v.muted, textDecoration: "none" }}
              >
                {l.label}
              </a>
            ) : (
              <span key={l.label} style={{ color: v.muted, cursor: "pointer" }}>
                {l.label}
              </span>
            ),
          )}
        </div>
        © 2026 Scrolly
      </div>
    </aside>
  );
}
